import { FC, useState, useEffect } from 'react';
import { Pencil, Trash2, X, RotateCcw, BarChart2, ImageOff, Building2, Target, Search, Ban } from 'lucide-react';
import { Property, PropertyStatus } from '@/types/index';
import clsx from 'clsx';
import { Badge } from '@components/common/Badge';
import { getStatusBadgeVariant, getStatusLabel } from '@utils/status-formatter';
import { pdfAnalysisService } from '@services/pdf-analysis.service';
import { calcDiffMarche } from '@utils/property-calcs';
import { computeFlipStatus } from '@/utils/flipViability';

interface PropertyTableProps {
  properties: Property[];
  onEdit: (property: Property) => void;
  onDelete: (id: string) => void;
  onReject?: (property: Property) => void;
  onRestore?: (property: Property) => void;
  onAddToPortfolio?: (property: Property) => void;
  onSimulate?: (property: Property) => void;
  isLoading?: boolean;
}

type SortColumn = 'id' | 'city' | 'askingPrice' | 'pricePerSqm' | 'market' | 'score' | 'flipViability' | 'area' | 'rooms' | 'status' | 'yield' | 'ppeScore' | 'daysOnMarket';
type SortDirection = 'asc' | 'desc';

function daysOnMarket(property: Property): number | null {
  const start = property.firstSeenAt ?? property.scrapedAt;
  if (!start) return null;
  const end = property.offMarketAt ?? new Date().toISOString();
  return Math.floor((new Date(end).getTime() - new Date(start).getTime()) / 86400000);
}

const getScoreBadgeColor = (score: number) => {
  if (score >= 8) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  if (score >= 6) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
  if (score >= 4) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
  if (score >= 2) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
};

export const PropertyTable: FC<PropertyTableProps> = ({
  properties,
  onEdit,
  onDelete,
  onReject,
  onRestore,
  onAddToPortfolio,
  onSimulate,
  isLoading = false,
}) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>('id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [ppeScores, setPpeScores] = useState<Record<string, number | null>>({});

  useEffect(() => {
    if (!properties.length) return;
    Promise.all(
      properties.map(p =>
        pdfAnalysisService.getIndicators(p.id)
          .then(data => ({ id: p.id, score: data?.globalScore ?? null }))
          .catch(() => ({ id: p.id, score: null }))
      )
    ).then(results => {
      const map: Record<string, number | null> = {};
      results.forEach(r => { map[r.id] = r.score; });
      setPpeScores(map);
    });
  }, [properties]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedProperties = [...properties].sort((a, b) => {
    let compareValue = 0;

    switch (sortColumn) {
      case 'id':
        compareValue = a.id.localeCompare(b.id);
        break;
      case 'city':
        compareValue = a.city.localeCompare(b.city);
        break;
      case 'askingPrice':
        compareValue = (a.askingPrice || 0) - (b.askingPrice || 0);
        break;
      case 'area':
        compareValue = a.area - b.area;
        break;
      case 'rooms':
        compareValue = a.rooms - b.rooms;
        break;
      case 'status':
        compareValue = a.status.localeCompare(b.status);
        break;
      case 'yield': {
        const aYield = a.yieldRate ?? a.estimatedYield ?? -1;
        const bYield = b.yieldRate ?? b.estimatedYield ?? -1;
        compareValue = aYield - bYield;
        break;
      }
      case 'pricePerSqm':
        compareValue = (a.pricePerM2 ?? -1) - (b.pricePerM2 ?? -1);
        break;
      case 'market': {
        const marketDelta = (p: Property) =>
          calcDiffMarche(p.askingPrice, p.estimatedSalePrice) ?? 999;
        compareValue = marketDelta(a) - marketDelta(b);
        break;
      }
      case 'score':
        compareValue = (a.opportunityScore ?? -1) - (b.opportunityScore ?? -1);
        break;
      case 'flipViability': {
        const order: Record<string, number> = { green: 0, orange: 1, red: 2 };
        const va = computeFlipStatus(a);
        const vb = computeFlipStatus(b);
        compareValue = (order[va ?? ''] ?? 3) - (order[vb ?? ''] ?? 3);
        break;
      }
      case 'ppeScore':
        compareValue = (ppeScores[a.id] ?? -1) - (ppeScores[b.id] ?? -1);
        break;
      case 'daysOnMarket':
        compareValue = (daysOnMarket(a) ?? -1) - (daysOnMarket(b) ?? -1);
        break;
    }

    return sortDirection === 'asc' ? compareValue : -compareValue;
  });

  const formatPrice = (price?: number) => {
    if (!price) return '—';
    return new Intl.NumberFormat('fr-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleDeleteClick = (property: Property) => {
    if (
      window.confirm(
        `Êtes-vous sûr de vouloir supprimer la propriété à ${property.address}, ${property.city} ?`
      )
    ) {
      onDelete(property.id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 dark:text-gray-400 mt-4">Chargement des propriétés...</p>
        </div>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
        <Building2 className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          Aucune propriété
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Commencez par ajouter votre première propriété.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* ── Mode cards — mobile < md ── */}
      <div className="md:hidden space-y-2">
        {sortedProperties.map((property, index) => {
          const loyerAnnuel = (property.rentPrice ?? 0) * 12;
          const rendBrut = property.askingPrice && property.isRented
            ? (loyerAnnuel / property.askingPrice) * 100 : null;

          return (
            <div
              key={property.id}
              className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-3 flex gap-3"
            >
              {/* Photo miniature */}
              {(() => {
                const primaryPhoto = property.photos.find(p => p.isPrimary) || property.photos[0];
                return primaryPhoto ? (
                  <img
                    src={`${import.meta.env.VITE_API_URL}${primaryPhoto.url}`}
                    alt=""
                    className="w-16 h-16 object-cover rounded-lg flex-shrink-0 border border-gray-200 dark:border-gray-700"
                  />
                ) : (
                  <div className="w-16 h-16 flex-shrink-0 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-300">
                    <ImageOff className="w-6 h-6" />
                  </div>
                );
              })()}

              {/* Infos */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {index + 1}. {property.city}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{property.address || '—'}</p>
                  </div>
                  <Badge variant={getStatusBadgeVariant(property.status)} size="sm">
                    {getStatusLabel(property.status)}
                  </Badge>
                </div>

                <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                  <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                    {formatPrice(property.askingPrice)}
                  </span>
                  {property.area && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">{property.area} m²</span>
                  )}
                  {property.rooms && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">{property.rooms} p.</span>
                  )}
                  {rendBrut != null && (
                    <span className="text-xs text-gray-600 dark:text-gray-400">{rendBrut.toFixed(1)}% brut</span>
                  )}
                  {property.opportunityScore != null && (
                    <span className={`text-xs font-bold ${
                      property.opportunityScore >= 70 ? 'text-emerald-600 dark:text-emerald-400' :
                      property.opportunityScore >= 45 ? 'text-amber-500' : 'text-red-500'
                    }`}>
                      Score {property.opportunityScore}/100
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-2 flex items-center gap-2">
                  <a href={`/analysis/${property.id}`}
                    className="px-2.5 py-1.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-xs font-semibold transition-colors"
                  >
                    Analyser
                  </a>
                  {onSimulate && (
                    <button onClick={() => onSimulate(property)} title="Simuler"
                      className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm transition-colors hover:bg-gray-50 dark:hover:bg-gray-800">
                      🎯
                    </button>
                  )}
                  <button onClick={() => onEdit(property)} title="Modifier" aria-label="Modifier"
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  {onReject && property.status !== PropertyStatus.REJECTED && (
                    <button onClick={() => onReject(property)} title="Rejeter" aria-label="Rejeter"
                      className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {onRestore && property.status === PropertyStatus.REJECTED && (
                    <button onClick={() => onRestore(property)} title="Restaurer" aria-label="Restaurer"
                      className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors">
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => handleDeleteClick(property)} title="Supprimer" aria-label="Supprimer"
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Mode tableau — md+ ── */}
    <div className="hidden md:block w-full rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
      <table className="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700 min-w-[1100px]">
        <colgroup>
          <col className="w-[3%]" />   {/* N° */}
          <col className="w-[6%]" />   {/* Ville */}
          <col className="w-[8%]" />   {/* Adresse */}
          <col className="w-[8%]" />   {/* Prix de vente */}
          <col className="w-[4%]" />   {/* Marché */}
          <col className="w-[5%]" />   {/* Score */}
          <col className="w-[3%]" />   {/* Flip */}
          <col className="w-[4%]" />   {/* Surface */}
          <col className="w-[7%]" />   {/* Prix/m² */}
          <col className="w-[3%]" />   {/* Pièces */}
          <col className="w-[8%]" />   {/* Rendement */}
          <col className="w-[4%]" />   {/* Score PPE */}
          <col className="w-[4%]" />   {/* Durée */}
          <col className="w-[8%]" />   {/* Status */}
          <col className="w-[5%]" />   {/* Photos */}
          <col className="w-[10%]" />  {/* Actions */}
        </colgroup>
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('id')}
            >
              <div className="flex items-center gap-1">
                N°
                {sortColumn === 'id' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('city')}
            >
              <div className="flex items-center gap-1">
                Ville
                {sortColumn === 'city' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
            >
              Adresse
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('askingPrice')}
            >
              <div className="flex items-center gap-1">
                Prix de vente
                {sortColumn === 'askingPrice' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Position par rapport au prix de marché estimé"
              onClick={() => handleSort('market')}
            >
              <div className="flex items-center gap-1">
                Marché
                {sortColumn === 'market' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Score d'opportunité (0–100) calculé selon votre profil investisseur"
              onClick={() => handleSort('score')}
            >
              <div className="flex items-center gap-1">
                Score
                {sortColumn === 'score' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            <th scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Viabilité flip calculée depuis les paramètres du scénario"
              onClick={() => handleSort('flipViability')}
            >
              <div className="flex items-center gap-1">
                Flip
                {sortColumn === 'flipViability' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('area')}
            >
              <div className="flex items-center gap-1">
                Surface
                {sortColumn === 'area' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Prix au m² (prix demandé / surface)"
              onClick={() => handleSort('pricePerSqm')}
            >
              <div className="flex items-center gap-1">
                Prix/m²
                {sortColumn === 'pricePerSqm' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('rooms')}
            >
              <div className="flex items-center gap-1">
                Pièces
                {sortColumn === 'rooms' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('yield')}
            >
              <div className="flex items-center gap-1">
                Rendement
                {sortColumn === 'yield' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('ppeScore')}
            >
              <div className="flex items-center gap-1">
                Score PPE
                {sortColumn === 'ppeScore' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Nombre de jours depuis le 1er scrape"
              onClick={() => handleSort('daysOnMarket')}
            >
              <div className="flex items-center gap-1">
                Durée
                {sortColumn === 'daysOnMarket' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort('status')}
            >
              <div className="flex items-center gap-1">
                Status
                {sortColumn === 'status' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
            >
              Photos
            </th>
            <th
              scope="col"
              className="px-2 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {sortedProperties.map((property, index) => {
            const loyerAnnuel = (property.rentPrice ?? 0) * 12;
            const chargesAnnuelles = (property.ppeCharges ?? 0) * 12;
            const rendBrut = property.askingPrice && property.isRented
              ? (loyerAnnuel / property.askingPrice) * 100 : null;
            const rendNet = property.askingPrice && property.isRented
              ? ((loyerAnnuel - chargesAnnuelles) / property.askingPrice) * 100 : null;

            return (
              <tr
                key={property.id}
                className={clsx(
                  'hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors',
                  index % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/50 dark:bg-gray-800/50'
                )}
              >
                <td className="px-2 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                  {index + 1}
                </td>
                <td className="px-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                  {property.city}
                </td>
                <td className="px-2 py-2 text-sm text-gray-700 dark:text-gray-300">
                  {property.address || '—'}
                </td>
                <td className="px-2 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {formatPrice(property.askingPrice)}
                </td>
                <td className="px-2 py-2 text-sm">
                  {property.estimatedSalePrice && property.askingPrice ? (() => {
                    const delta = calcDiffMarche(property.askingPrice, property.estimatedSalePrice)!;
                    const isUnder = delta < -10;
                    const isOver = delta > 10;
                    return (
                      <span
                        className={`text-xs font-semibold ${isUnder ? 'text-green-600 dark:text-green-400' : isOver ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}
                        title={`Prix marché estimé : ${property.estimatedSalePrice.toLocaleString('fr-CH')} CHF [${(property.estimatedSalePriceMin ?? 0).toLocaleString('fr-CH')}–${(property.estimatedSalePriceMax ?? 0).toLocaleString('fr-CH')}]`}
                      >
                        {isUnder ? '▼' : isOver ? '▲' : '●'} {delta > 0 ? '+' : ''}{delta.toFixed(0)}%
                      </span>
                    );
                  })() : <span className="text-gray-300 dark:text-gray-600">—</span>}
                </td>
                <td className="px-2 py-2 text-sm font-bold">
                  {property.opportunityScore != null ? (
                    <span className={
                      property.opportunityScore >= 70 ? 'text-emerald-600 dark:text-emerald-400' :
                      property.opportunityScore >= 45 ? 'text-amber-500 dark:text-amber-400' :
                      'text-red-500 dark:text-red-400'
                    }>
                      {property.opportunityScore}<span className="text-[9px] font-normal text-gray-400 dark:text-gray-500">/100</span>
                    </span>
                  ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                </td>
                <td className="px-2 py-2 text-base">
                  {(() => {
                    const fs = computeFlipStatus(property);
                    if (fs === null) return <span className="text-gray-300 dark:text-gray-600 text-sm">—</span>;
                    return (
                      <span title={
                        fs === 'green'  ? 'Flip viable au prix affiché' :
                        fs === 'orange' ? 'Négociation nécessaire (écart ≤ 20%)' :
                                         'Écart trop important — PMA irréaliste'
                      }>
                        {fs === 'green' ? '🟢' : fs === 'orange' ? '🟠' : '🔴'}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                  {property.area} m²
                </td>
                <td className="px-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                  {property.pricePerM2 != null
                    ? `${Math.round(property.pricePerM2).toLocaleString('fr-CH')} /m²`
                    : '—'}
                </td>
                <td className="px-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                  {property.rooms}
                </td>
                <td className="px-2 py-2">
                  {rendBrut != null ? (
                    <div className="text-xs space-y-0.5">
                      <div className="font-medium text-gray-800 dark:text-gray-200">{rendBrut.toFixed(1)}% brut</div>
                      <div className="text-gray-500 dark:text-gray-400">{rendNet!.toFixed(1)}% net</div>
                    </div>
                  ) : property.estimatedYield != null ? (
                    <div className="text-xs space-y-0.5" title={`Rendement estimé (confiance : ${property.rentConfidence ?? '?'})`}>
                      <div className="font-medium text-amber-600 dark:text-amber-400">~{property.estimatedYield.toFixed(1)}% brut</div>
                      <div className="text-gray-400 dark:text-gray-500">{property.estimatedRent?.toLocaleString('fr-CH')} CHF/mois</div>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">—</span>
                  )}
                </td>
                <td className="px-2 py-2">
                  {!(property.id in ppeScores) ? (
                    <span className="text-gray-300 dark:text-gray-600 text-xs">…</span>
                  ) : ppeScores[property.id] != null ? (
                    <span className={`px-2 py-1 rounded-lg text-sm font-bold ${getScoreBadgeColor(ppeScores[property.id]!)}`}>
                      {ppeScores[property.id]}/10
                    </span>
                  ) : (
                    <span className="text-gray-400 text-sm">—</span>
                  )}
                </td>
                <td className="px-2 py-2 text-sm">
                  {(() => {
                    const days = daysOnMarket(property);
                    if (days === null) return <span className="text-gray-400">—</span>;
                    const color = days < 14
                      ? 'text-green-600 dark:text-green-400'
                      : days < 60
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-red-600 dark:text-red-400';
                    return (
                      <span className={`font-semibold ${color}`} title={property.firstSeenAt ? `Depuis le ${new Date(property.firstSeenAt).toLocaleDateString('fr-CH')}` : ''}>
                        {days}j
                      </span>
                    );
                  })()}
                </td>
                <td className="px-2 py-2 text-sm">
                  {property.isOffMarket ? (
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 whitespace-nowrap">
                      Retiré{property.offMarketAt ? ` le ${new Date(property.offMarketAt).toLocaleDateString('fr-CH')}` : ''}
                    </span>
                  ) : (
                    <Badge
                      variant={getStatusBadgeVariant(property.status)}
                      size="sm"
                    >
                      {getStatusLabel(property.status)}
                    </Badge>
                  )}
                </td>
                <td className="px-2 py-2 align-middle">
                  <div className="flex items-center justify-start">
                    {(() => {
                      const primaryPhoto = property.photos.find((p) => p.isPrimary) || property.photos[0];
                      const photoCount = property.photos.length;

                      if (!primaryPhoto) {
                        return <ImageOff className="w-7 h-7 text-gray-300 dark:text-gray-600" />;
                      }

                      return (
                        <div className="relative w-9 h-9 shrink-0">
                          <img
                            src={`${import.meta.env.VITE_API_URL}${primaryPhoto.url}`}
                            alt="Preview"
                            className="w-9 h-9 object-cover rounded border border-gray-200 dark:border-gray-700 block"
                          />
                          <span className="absolute bottom-0.5 right-0.5 bg-gray-700/80 dark:bg-gray-600/80 text-white text-[9px] font-medium rounded px-0.5 leading-tight min-w-[14px] text-center">
                            {photoCount}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </td>
                <td className="px-2 py-2 text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-1">
                    {onSimulate && (
                      <button
                        onClick={() => onSimulate(property)}
                        className="p-1.5 rounded text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                        title="Simuler l'acquisition"
                        aria-label="Simuler"
                      >
                        <Target className="w-4 h-4" />
                      </button>
                    )}
                    {onAddToPortfolio && (
                      <button
                        onClick={() => onAddToPortfolio(property)}
                        className="p-1.5 rounded text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                        title="Ajouter au portefeuille"
                        aria-label="Ajouter au portefeuille"
                      >
                        <BarChart2 className="w-4 h-4" />
                      </button>
                    )}
                    <a
                      href={`/analysis/${property.id}`}
                      className="p-1.5 rounded text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      title="Analyser"
                      aria-label="Analyser"
                    >
                      <Search className="w-4 h-4" />
                    </a>
                    {onReject && property.status !== PropertyStatus.REJECTED && property.status !== PropertyStatus.PURCHASED && (
                      <button
                        onClick={() => onReject(property)}
                        className="p-1.5 rounded text-orange-500 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                        title="Rejeter"
                        aria-label="Rejeter"
                      >
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                    {onRestore && property.status === PropertyStatus.REJECTED && (
                      <button
                        onClick={() => onRestore(property)}
                        className="p-1.5 rounded text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                        title="Restaurer en 1ère sélection"
                        aria-label="Restaurer"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(property)}
                      className="p-1.5 rounded text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      title="Modifier"
                      aria-label="Modifier"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(property)}
                      className="p-1.5 rounded text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title="Supprimer"
                      aria-label="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
    </>
  );
};
